/**
 * DesktopNav.tsx
 *
 * Responsive desktop navigation bar used in the site header.
 * Features:
 * - Automatic active-state detection via Next.js router
 * - HoverEffect background animation layer
 * - Scrollable horizontal nav (hidden scrollbar on supported devices)
 * - Fully compatible with Node 18–24 and Next.js 14
 *
 * Depends on:
 * - <Link />              : local navigation component
 * - <HoverEffect />       : interactive background hover animation
 * - HEADER.nav            : navigation configuration array
 */

import { FC, memo } from "react";
import { useRouter } from "next/router";
import clsx from "clsx";

import { Link } from "components/link";
import { HoverEffect } from "components/layout/header.desktop-nav.hover-effect";
import { HEADER } from "content/layout";

/**
 * Desktop navigation bar for medium+ screens.
 * Hidden on mobile; MobileNav handles small screens.
 */
export const DesktopNav: FC = memo(() => {
  const router = useRouter();

  return (
    <nav
      className="
        header-nav group relative isolate mt-auto hidden h-full 
        justify-center overflow-auto px-2 md:flex 
        sm:scrollbar-none
      "
    >
      {/* Background animation layer */}
      <HoverEffect />

      {HEADER.nav
        .filter(({ desktop }) => desktop)
        .map((item, index) => {
          // Clean path: remove ?query and #hash
          const currentPath = router.asPath.split(/[#?]/)[0];
          const isActive = currentPath === item.href;

          return (
            <div
              key={`${item.href}-${index}`}
              className="my-auto flex h-full items-center px-2"
            >
              <Link
                href={item.href}
                className={clsx(
                  `
                    relative z-10 flex hfull items-center
                    border-2 border-transparent 
                    py-1.5 px-4 text-gray-500 transition-all 
                    hover:text-gray-900 focus-visible:outline-none 
                    dark:text-gray-300 dark:hover:text-gray-50
                  `,
                  isActive &&
                  `
                      border-gray-700/5 bg-gray-100 bg-clip-padding 
                      dark:border-gray-50/10 dark:bg-gray-800 
                      group-hover:bg-gray-200/30
                    `
                )}
              >
                <span className="text-sm font-medium">{item.title}</span>
              </Link>
            </div>
          );
        })}
    </nav>
  );
});

DesktopNav.displayName = "DesktopNav";

export default DesktopNav;
